import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  IconButton,
  Grid,
  Paper,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const pledgeOptions = [
  'actions.option1',
  'actions.option2',
  'actions.option3',
  'actions.option4',
];

const Actions = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [emailError, setEmailError] = useState('');


  const handleOptionChange = (option) => {
    setSelectedOptions((prev) =>
      prev.includes(option)
        ? prev.filter((o) => o !== option)
        : [...prev, option]
    );
  };

const handleSubmit = async () => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    setEmailError('Email should be valid');
    return;
  }

  setEmailError('');

  // Create the email content
  const subject = 'Vision Zero Pledge Submission';
const html = `
  <div style="font-family: Arial, sans-serif; max-width: 650px; margin: auto; border: 1px solid #ddd; border-radius: 8px; padding: 24px; background-color: #fefefe;">
    <h2 style="text-align: center; color: #333;">🙏 New Pledge Submission</h2>

    <hr style="border: none; border-top: 1px solid #ccc; margin: 20px 0;" />

    <p style="font-size: 16px; color: #444;"><strong>First Name:</strong> ${firstName}</p>
    <p style="font-size: 16px; color: #444;"><strong>Last Name:</strong> ${lastName}</p>
    <p style="font-size: 16px; color: #444;"><strong>Email:</strong> ${email}</p>

    <div style="margin-top: 20px;">
      <p style="font-size: 16px; color: #444;"><strong>Selected Pledges:</strong></p>
      <ul style="padding-left: 20px; font-size: 15px; color: #555;">
        ${selectedOptions.map((key) => `<li>${t(key)}</li>`).join('')}
      </ul>
    </div>

    <hr style="border: none; border-top: 1px solid #ccc; margin: 20px 0;" />

    <p style="font-size: 13px; color: #888; text-align: center;">This pledge was submitted from Miami Lakes Vision Zero website form.</p>
  </div>
`;


  try {
    // Send email request
    await fetch('http://localhost:5000/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        toEmail: ['transportationlist@miamilakes-fl.gov'], // replace with your real email(s)
        subject,
        html,
      }),
    });
  } catch (error) {
    console.error('Error sending email:', error);
    // You can log or report the error if needed
  } finally {
    // Always show the submitted screen regardless of success or failure
    setSubmitted(true);
  }
};



  const resetForm = () => {
    setStep(1);
    setSelectedOptions([]);
    setFirstName('');
    setLastName('');
    setEmail('');
    setSubmitted(false);
  };

  return (
    <Box>
      {/* Intro Paragraph */}
      <Box component="main" sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h3" sx={{ fontSize: { xs: '2rem', md: '2.5rem' }, mt: -2, mb: 4 }}>
          {t('actions.title', 'ACTIONS')}
        </Typography>
        <Typography
          variant="body1"
          sx={{ maxWidth: '75%', mx: 'auto', textAlign: 'justify', fontSize: { xs: '1rem', sm: '1.25rem' }, lineHeight: 1.6 }}
        >
          {t('actions.description')}
        </Typography>
      </Box>

      {/* Pledge Section */}
      <Box component="section" sx={{ p: 4, textAlign: 'center', backgroundColor: '#F5F5F5' }}>
        <Typography variant="h3" sx={{ fontSize: { xs: '2rem', md: '2.5rem' }, mb: 2 }}>
          {t('actions.pledge_title')}
        </Typography>
        <Typography
          variant="body1"
          sx={{ maxWidth: '75%', mx: 'auto', textAlign: 'justify', fontSize: { xs: '1rem', sm: '1.25rem' }, lineHeight: 1.6, mb: 4 }}
        >
          {t('actions.pledge_description')}
        </Typography>

        {/* Step 1: Checkboxes */}
        {!submitted && step === 1 && (
          <Paper
  elevation={3}
  sx={{
    p: 3,
    maxWidth: {
      xs: '95%',  // for mobile (xs breakpoint)
      sm: '75%',  // for tablets and small screens
      md: '65%'   // for medium and up (your current default)
    },
    mx: 'auto',
    borderRadius: 2
  }}
>

            <Box sx={{ backgroundColor: '#00539b', p: 2, 
                borderRadius: 1 }}>
              <Typography variant="h5" sx={{ color: 'white', mb: 1 }}>{t('actions.select_pledge')}</Typography>
            </Box>
<Grid
  container
  spacing={2}
  direction="column"
  justifyContent="center"
  alignItems="center" // center grid items horizontally
  sx={{ mt: 2 }}
>
  {pledgeOptions.map((key) => (
    <Grid item key={key} 
    sx={{ 
        width: {
                  xs: '100%',  // for mobile (xs breakpoint)
      sm: '75%',  // for tablets and small screens
      md: '65%'   // for medium and up (your current default)
        } 
        
        }}>
      <Box
        sx={{
          border: '1px solid #ccc',
          borderRadius: 2,
          p: 2,
          textAlign: 'left',
          transition: 'transform 0.2s ease',
          '&:hover': { transform: 'scale(1.03)', borderColor: 'primary.main' },
          '&.selected': { borderColor: 'primary.main' },
        }}
        className={selectedOptions.includes(key) ? 'selected' : ''}
      >
        <FormControlLabel
          control={<Checkbox checked={selectedOptions.includes(key)} onChange={() => handleOptionChange(key)} />}
          label={t(key)}
        />
      </Box>
    </Grid>
  ))}
</Grid>

            <Box textAlign="right" mt={3}>
              <Button
                variant="text"
                endIcon={<ArrowForwardIcon />}
                onClick={() => setStep(2)}
                sx={{ color: '#00b9c4', backgroundColor: 'white' }}
              >
                {t('actions.next')}
              </Button>
            </Box>
          </Paper>
        )}

        {/* Step 2: Name Inputs */}
{step === 2 && (
  <Paper
    elevation={3}
    sx={{
      p: 3,
      maxWidth: {
        xs: '95%',
        sm: '75%',
        md: '65%',
      },
      mx: 'auto',
      borderRadius: 2,
      mt: 2,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      minHeight: '400px', // adjust as needed
    }}
  >
    <Box sx={{ maxWidth: 600, width: '100%', mx: 'auto' }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h6">{t('actions.name')}</Typography>
      </Box>

      <Grid container spacing={2} justifyContent="center">
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('actions.first_name')}
            variant="standard"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('actions.last_name')}
            variant="standard"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </Grid>
      </Grid>
    </Box>

    {/* Navigation Buttons */}
    <Box
      mt="auto"
      pt={3}
      display="flex"
      justifyContent="space-between"
      alignItems="center"
    >
      <IconButton onClick={() => setStep(1)} sx={{ color: '#00b9c4' }}>
        <ArrowBackIcon />
      </IconButton>

      <Button
        variant="text"
        endIcon={<ArrowForwardIcon />}
        onClick={() => setStep(3)}
        sx={{ color: '#00b9c4', backgroundColor: 'white' }}
      >
        {t('actions.next')}
      </Button>
    </Box>
  </Paper>
)}



        {/* Step 3: Email & Submit */}
{step === 3 && !submitted && (
  <Paper
    elevation={3}
    sx={{
      p: 3,
      maxWidth: {
        xs: '95%',
        sm: '75%',
        md: '65%',
      },
      mx: 'auto',
      borderRadius: 2,
      mt: 2,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      minHeight: '400px', // Same height as Step 2
    }}
  >
    <Box sx={{ maxWidth: 600, width: '100%', mx: 'auto' }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h6">{t('actions.email')}</Typography>
      </Box>

<TextField
  fullWidth
  label={t('actions.email')}
  variant="standard"
  value={email}
  onChange={(e) => setEmail(e.target.value)}
  error={Boolean(emailError)}
  helperText={emailError}
/>

    </Box>

    {/* Navigation Buttons */}
    <Box
      mt="auto"
      pt={3}
      display="flex"
      justifyContent="space-between"
      alignItems="center"
    >
      <IconButton onClick={() => setStep(2)} sx={{ color: '#00b9c4' }}>
        <ArrowBackIcon />
      </IconButton>

<Button
  variant="text"
  onClick={handleSubmit}
  sx={{ color: '#00b9c4', backgroundColor: 'white' }}
>
  {t('actions.submit')}
</Button>

    </Box>
  </Paper>
)}


        {/* Submission Message */}
{submitted && (
  <Paper
    elevation={3}
    sx={{
      p: 3,
      maxWidth: {
        xs: '95%',
        sm: '75%',
        md: '65%',
      },
      mx: 'auto',
      borderRadius: 2,
      mt: 2,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '400px', // Match height with previous steps
      textAlign: 'center',
      gap:4
    }}
  >
    <img
      src="images//confirmation.png"
      alt="Success"
      style={{ maxWidth: '100%', height: '10vh', marginBottom: 16 }}
    />

    <Typography variant="h6" gutterBottom>
      {t('actions.thank_you', 'Thank you! Your response has been submitted.')}
    </Typography>

<Button
  onClick={resetForm}
  sx={{
    mt: 2,
    backgroundColor: 'rgb(36, 166, 138)',
    color: 'white',
    borderRadius: '30px',
    paddingX: 3,
    paddingY: 1,
    '&:hover': {
      backgroundColor: 'rgb(30, 145, 121)', // Slightly darker on hover
    },
  }}
>
  {t('actions.add_another', 'Add another response')}
</Button>
  </Paper>
)}

      </Box>
    </Box>
  );
};

export default Actions;
